import {
	Button,
	CardActions,
	CardContent,
	FormHelperText,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useFieldArray,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField, {
	InputType,
} from '@/common/components/form-inputs/AppTextField';
import { useNavigate, useParams } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddRewardItemDialog from './AddRewardItemDialog';
import { AwardType } from '@/api/enum/common.enum';
import React from 'react';
import { TaskAchievementRewardDetailSingleRequest } from '@/api/DTO/task/achievement.interface';
import {
	useCreateAchievementSingleMutation,
	useUpdateAchievementSingleMutation,
} from '@/api/task/achievementApiSlice';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

interface RewardItem {
	rewardId: string;
	name: string;
	count: string;
	[key: string]: any;
}

export default function SingleInfoForm({ type, setType, details }: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const [createAchievementSingle] = useCreateAchievementSingleMutation();
	const [updateAchievementSingle] = useUpdateAchievementSingleMutation();
	const { achievementId, achievementSingleId } = useParams();

	const awardTypeList = [
		{ id: AwardType.Int, name: t('taskManage.Int') },
		{ id: AwardType.Prop, name: t('taskManage.Prop') },
		{ id: AwardType.IntProp, name: t('taskManage.IntProp') },
	];

	const defaultValues: DefaultValues<TaskAchievementRewardDetailSingleRequest> =
		{ rewardType: details?.rewardType || -1 };
	useEffect(() => {
		if (details?.rewardType > -1) {
			methods.setValue('rewardType', details.rewardType);
		}

		if (Number(details?.finishedNum) > -1)
			methods.setValue('finishedNum', details?.finishedNum || '');
		if (Number(details?.rewardPoints) > -1)
			methods.setValue('rewardPoints', details?.rewardPoints);
		if (details?.rewardList) {
			const arr = details.rewardList.map((item: any) => {
				return {
					...item,
					titleZh: item.seriesTitleZh + ' - ' + item.titleZh,
				};
			});
			methods.setValue('rewardList', arr);

			optionsReplace(arr);
		}
	}, [details]);

	const formSchema = Yup.object().shape({
		finishedNum: Yup.string().required(t('errorMessage.required')),
		rewardType: Yup.string().required(t('errorMessage.pleaseSelect')),
		rewardPoints: Yup.string()
			.nullable()
			.when('rewardType', {
				is: (rewardType: string | number) =>
					rewardType == AwardType.Int || rewardType == AwardType.IntProp,
				then: (schema) => schema.required(t('errorMessage.required')),
			}),
		rewardList: Yup.array()
			.nullable()
			.when('rewardType', {
				is: (rewardType: string | number) =>
					rewardType == AwardType.Prop || rewardType == AwardType.IntProp,
				then: (schema) =>
					schema
						.required(t('errorMessage.pleaseSelect'))
						.min(1, t('errorMessage.pleaseSelect')),
				otherwise: (schema) => schema.nullable().notRequired(),
			}),
	});
	const methods = useForm<TaskAchievementRewardDetailSingleRequest>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const watchRewardType = methods.watch('rewardType');

	const {
		fields: rewardListField,
		append: optionsAppend,
		remove: optionsRemove,
		update: optionsUpdate,
		replace: optionsReplace,
	}: any = useFieldArray({
		control: methods.control,
		name: 'rewardList',
	});

	useEffect(() => {
		const subscription = methods.watch((value) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<
		TaskAchievementRewardDetailSingleRequest
	> = async (data) => {
		const obj = { ...data };
		obj.rewardList = methods
			.getValues('rewardList')
			?.map((item: { id: any }) => {
				delete item.id;
				return {
					...item,
				};
			});
		if (Number(data.rewardType) == AwardType.Int) {
			delete obj.rewardList;
		}
		if (Number(data.rewardType) == AwardType.Prop) {
			delete obj.rewardPoints;
		}
		const response =
			type == FormType.create
				? await createAchievementSingle({
						...obj,
						seriesId: achievementId,
				  })
				: await updateAchievementSingle({
						...obj,
						id: achievementSingleId || '',
						seriesId: achievementId,
				  });
		if ('data' in response) {
			enqueueSnackbar(
				type == FormType.create
					? t('snackbar.addTaskSuccess')
					: t('snackbar.changeTaskSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			if (type == FormType.create) {
				navigate(`/task-manage/achievement-list/info/${achievementId}`);
			} else {
				setType(FormType.view);
			}
		}
	};

	const handleClose = () => {
		if (type == FormType.edit) return setType(FormType.view);
		navigate(`/task-manage/achievement-list/info/${achievementId}`);
	};

	const handleGetRewardList = (list: RewardItem[]) => {
		if (list.length > 0) {
			const arr = methods.getValues('rewardList') || [];
			arr.push(list[0]);
			optionsReplace(arr);
		}
	};

	const handleDeleteReward = (index: number) => {
		optionsRemove(index);
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={6}>
								<AppFieldView label={t('achievementList.level')} value={'-'} />
							</Grid>
							<Grid item xs={6}>
								<AppTextField
									inputType={InputType.number}
									control={methods.control}
									name='finishedNum'
									label={t('taskManage.total')}
								/>
							</Grid>
							<Grid item xs={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('taskManage.awardType')}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='rewardType'
									row
									options={awardTypeList}
								/>
							</Grid>
							{[AwardType.Int, AwardType.IntProp].includes(
								Number(watchRewardType)
							) && (
								<Grid item xs={6}>
									<AppTextField
										control={methods.control}
										name='rewardPoints'
										label={t('taskManage.rewardPoints')}
										inputType={InputType.number}
										maxLength={5}
									/>
								</Grid>
							)}
							{[AwardType.Prop, AwardType.IntProp].includes(
								Number(watchRewardType)
							) &&
								rewardListField.map((item: any, index: number) => (
									<React.Fragment key={item.rewardId}>
										{index == 0 &&
											Number(watchRewardType) == AwardType.Prop && (
												<Grid item xs={6}></Grid>
											)}
										<Grid item xs={6}>
											<AppTextField
												disabled
												control={methods.control}
												name={`rewardList.${index}.titleZh`}
												label={t('regularTask.rewardItem')}
											/>
										</Grid>
										<Grid item xs={6} sx={{ display: 'flex' }}>
											<AppTextField
												control={methods.control}
												name={`rewardList.${index}.rewardNum`}
												label={t('regularTask.rewardItemCount')}
												inputType={InputType.number}
											/>
											<IconButton
												aria-label='close'
												onClick={() => handleDeleteReward(index)}
												color='error'
											>
												<DeleteOutlineOutlinedIcon />
											</IconButton>
										</Grid>
									</React.Fragment>
								))}
							{[AwardType.Prop, AwardType.IntProp].includes(
								Number(watchRewardType)
							) && (
								<Grid item xs={12}>
									<AddRewardItemDialog
										handleGetRewardList={handleGetRewardList}
									/>
									<FormHelperText style={{ color: '#d32f2f' }}>
										{typeof methods.formState.errors.rewardList?.message ===
										'string'
											? methods.formState.errors.rewardList.message
											: ''}
									</FormHelperText>
								</Grid>
							)}
						</Grid>
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							disabled={!needConfirm && type === FormType.create}
						>
							{type === FormType.edit ? t('button.save') : t('button.add')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
